import { formatRussianWeeks } from "./russianPlural";
import type { WeeklyStreakAward, WeeklyStreakState } from "../types/streak";
export type { WeeklyStreakAward } from "../types/streak";

// Older streak rewards are already part of the user's aggregate XP baseline.
// Only rewards from this week onward should be exposed as new activity events.
export const WEEKLY_STREAK_EVENT_START_WEEK = "2026-08-03";

const normalizeWeekKey = (value: string) => {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? match[0] : "";
};

const getSignedAmount = (value: number) => {
  const amount = Number(value) || 0;
  return amount > 0 ? `+${amount}` : String(amount);
};

const toNonNegativeExperience = (value: unknown) => {
  const experience = Number(value);
  return Number.isFinite(experience) ? Math.max(0, experience) : 0;
};

const addExperience = (total: number, value: number|undefined) => Math.min(
  Number.MAX_SAFE_INTEGER,
  toNonNegativeExperience(total) + toNonNegativeExperience(value)
);

export const getWeeklyStreakEventKey = (weekKey: string, experience: number) => {
  const normalizedWeekKey = normalizeWeekKey(weekKey);
  const normalizedExperience = Number(experience) || 0;
  if (!normalizedWeekKey || !normalizedExperience) return "";
  return `weekly-streak:${normalizedWeekKey}:${getSignedAmount(normalizedExperience)}`;
};

export const getWeeklyStreakExperienceComponentId = (weekKey: string) => {
  const normalizedWeekKey = normalizeWeekKey(weekKey);
  return normalizedWeekKey ? `weekly_streak:${normalizedWeekKey}` : "";
};

// Versioned streak state uses one amount-bearing target per week. XP is the
// preserved pre-rollout baseline plus the exact sum of those targets; neither
// the mutable current count nor the historical best count is an XP source.
export const getWeeklyStreakLedgerExperience = (weeklyStreak: Partial<WeeklyStreakState> | null = {}) => {
  const awardedWeeks = Object.entries<WeeklyStreakAward>(weeklyStreak?.awardedWeeks || {});
  const isReplayLedger = Number(weeklyStreak?.streakReplayVersion) >= 1;

  if (isReplayLedger) {
    return awardedWeeks
      .filter(([weekKey]) => {
        const normalizedWeekKey = normalizeWeekKey(weekKey);
        return normalizedWeekKey >= WEEKLY_STREAK_EVENT_START_WEEK;
      })
      .reduce(
        (total, [, award]) => addExperience(total, award?.bonus),
        toNonNegativeExperience(weeklyStreak?.legacyStreakBonusTotal)
      );
  }

  if (Object.prototype.hasOwnProperty.call(weeklyStreak || {}, "streakBonusTotal")) {
    return toNonNegativeExperience(weeklyStreak?.streakBonusTotal);
  }

  return awardedWeeks.reduce(
    (total, [, award]) => addExperience(total, award?.bonus),
    0
  );
};

export const buildWeeklyStreakEvents = (weeklyStreak: Partial<WeeklyStreakState> | null = {}) =>
  Object.entries<WeeklyStreakAward>(weeklyStreak?.awardedWeeks || {})
    .map(([weekKey, award]) => {
      const normalizedWeekKey = normalizeWeekKey(weekKey);
      const experience = Number(award?.bonus) || 0;
      const streakWeekNumber = Math.max(0, Math.trunc(Number(award?.streakWeekNumber) || 0));
      if (
        !normalizedWeekKey ||
        normalizedWeekKey < WEEKLY_STREAK_EVENT_START_WEEK ||
        !experience ||
        !streakWeekNumber
      ) {
        return null;
      }

      return {
        componentId: getWeeklyStreakExperienceComponentId(normalizedWeekKey),
        dedupeKey: getWeeklyStreakEventKey(normalizedWeekKey, experience),
        weekKey: normalizedWeekKey,
        streakWeekNumber,
        experience,
        title: experience > 0
          ? `Серия продлена: ${formatRussianWeeks(streakWeekNumber)}`
          : `Серия скорректирована: ${formatRussianWeeks(streakWeekNumber)}`,
        description: experience > 0
          ? "Награда за активную неделю"
          : "Корректировка награды за недельную серию"
      };
    })
    .filter((event): event is NonNullable<typeof event> => event !== null)
    .sort((left, right) =>
      left.weekKey.localeCompare(right.weekKey) || left.experience - right.experience
    );
